import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { BasicButton } from '../../../components/common/Button';
import { InputCheckbox, InputWord } from '../../../components/common/Input';
import { PasswordConstraint } from '../../../components/common/PasswordConstraint';
import FormHeader from '../../../components/forms/FormHeader';
import { subscriptionSchema, subscriptionType } from '../../../schema/SubscriptionSchema';
import UsersService from '../../../services/users.service';

type SubscriptionFormProps = {
  setModaleCreatedAccountOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const SubscriptionForm = ({ setModaleCreatedAccountOpen }: SubscriptionFormProps) => {
  const navigate: NavigateFunction = useNavigate();
  const [passwordFocus, setPasswordFocus] = useState<boolean>(false);
  const usersService = new UsersService();
  const searchQueryParams = useSearchParams()[0];

  const subscriptionMethods = useForm<subscriptionType>({
    resolver: yupResolver(subscriptionSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: decodeURIComponent(searchQueryParams.get('mail') ?? ''),
      nickname: '',
      password: '',
      confirmPassword: '',
      acceptCGU: false,
      subscribeNewsletter: false,
    },
  });

  const { watch } = subscriptionMethods;

  const createUser = async ({ email, nickname, password, subscribeNewsletter }: subscriptionType) => {
    await usersService.createUser({
      email,
      nickName: nickname,
      password,
      subscribeNewsletter,
    });

    setModaleCreatedAccountOpen(true);
  };

  return (
    <>
      <FormHeader
        topLabel='Déjà inscrit ?'
        actionTopLabel='Connectez-vous ici'
        actionTopOnClick={() => navigate(`/login`)}
        title='Créer votre compte'
        addAppsConnect={true}
      />
      <FormProvider {...subscriptionMethods}>
        <form onSubmit={subscriptionMethods.handleSubmit(createUser)}>
          <Controller
            name='email'
            control={subscriptionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Email'
                  type='text'
                  mandatory={true}
                  placeholder='Email'
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          <Controller
            name='nickname'
            control={subscriptionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Identifiant'
                  type='text'
                  mandatory={true}
                  tooltip={true}
                  placeholder='Identifiant'
                  value={value}
                  onChangeValue={(value: string) => onChange(value.toLowerCase())}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          <Controller
            name='password'
            control={subscriptionMethods.control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Mot de passe'
                  type='password'
                  autoComplete='off'
                  mandatory={true}
                  placeholder='Mot de passe'
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                  onFocus={() => {
                    setPasswordFocus(true);
                  }}
                  onBlur={() => {
                    setPasswordFocus(false);
                    onBlur();
                  }}
                />
              </>
            )}
          />
          {passwordFocus && <PasswordConstraint password={watch('password')} />}
          <Controller
            name='confirmPassword'
            control={subscriptionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Confirmer le mot de passe'
                  type='password'
                  autoComplete='off'
                  placeholder='Mot de passe'
                  mandatory={true}
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          <Controller
            name='acceptCGU'
            control={subscriptionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputCheckbox
                  label={
                    <span className='text-xs'>
                      J’ai pris connaissance et accepte les{' '}
                      <span
                        onClick={() => window.open('/conditions-generales-utilisation.pdf', '_blank')}
                        className='text-green-secondary cursor-pointer font-medium hover:font-semibold hover:underline'
                      >
                        Conditions Générales d’Utilisation
                      </span>{' '}
                      et la{' '}
                      <span
                        onClick={() => window.open('/politique-de-confidentialite.pdf', '_blank')}
                        className='text-green-secondary cursor-pointer font-medium hover:font-semibold hover:underline'
                      >
                        Politique de confidentialité
                      </span>
                    </span>
                  }
                  checked={value}
                  actionOnClick={() => onChange(!value)}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          <Controller
            name='subscribeNewsletter'
            control={subscriptionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputCheckbox
                  label={<span className='text-xs'>Je souhaite être informé des futures évolutions et actualités des solutions</span>}
                  checked={value}
                  actionOnClick={() => onChange(!value)}
                  errorMessage={error?.message}
                />
              </>
            )}
          />
          <BasicButton label="S'inscrire" fullWidth={true} isSubmitButton={true} />
        </form>
      </FormProvider>
    </>
  );
};

export default SubscriptionForm;
